import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CalendarClock, CheckCheck, Plane, UserMinus, UserPlus, UserX, Users } from 'lucide-react';
import { NOTIFICATION_KINDS, NOTIFICATION_LABELS, type NotificationItem, type NotificationKind, type NotificationList, type NotificationSettings } from '@hoopify/shared';
import { api, errorMessage } from '../../lib/api';
import { timeAgo } from '../../lib/format';
import { Alert, Button, LoadingBlock, Section } from '../ui';

const KEY = ['notifications'];
const ICONS: Record<NotificationKind, typeof Bell> = { unavailable: UserX, away: Plane, coming: Users, joined: UserPlus, left: UserMinus, join_request: UserPlus, fixture: CalendarClock };

function useNotifications() {
  return useQuery({ queryKey: KEY, queryFn: () => api.get<NotificationList>('/notifications'), refetchInterval: 60_000, refetchOnWindowFocus: true, staleTime: 20_000 });
}

/** The bell: unread count, and a list that opens below it. */
export function NotificationBell({ align = 'right' }: { align?: 'left' | 'right' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const q = useNotifications();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const read = useMutation({ mutationFn: (ids?: string[]) => api.post<NotificationList>('/notifications/read', ids ? { ids } : {}), onSuccess: (l) => qc.setQueryData(KEY, l) });
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const unread = q.data?.unread ?? 0;
  const pick = (n: NotificationItem) => {
    if (!n.read) read.mutate([n.id]);
    setOpen(false);
    if (n.link) navigate(n.link);
  };
  return (
    <div className="relative" ref={ref}>
      <button
        className="relative grid size-10 place-items-center rounded-md text-muted hover:bg-sunken hover:text-ink"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        title="Notifications"
      >
        <Bell className="size-5" aria-hidden />
        {unread > 0 && (
          <span className="tnum absolute top-1 right-1 grid min-w-[18px] place-items-center rounded-full bg-pink px-1 text-[11px] leading-[18px] font-bold text-white" aria-hidden>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className={`fixed inset-x-3 top-16 z-50 rounded-lg border border-line bg-surface shadow-xl sm:absolute sm:inset-x-auto sm:top-11 sm:w-[380px] ${align === 'right' ? 'sm:right-0' : 'sm:left-0'}`}
        >
          <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
            <p className="font-semibold">Notifications</p>
            {unread > 0 && (
              <button className="inline-flex items-center gap-1 text-[13px] font-medium text-action hover:underline" onClick={() => read.mutate(undefined)}>
                <CheckCheck className="size-4" aria-hidden /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {q.isLoading ? (
              <LoadingBlock />
            ) : !q.data?.items.length ? (
              <p className="px-4 py-8 text-center text-[14px] text-muted">Nothing yet. You’ll see it here when a player can’t make it, someone joins, and so on.</p>
            ) : (
              <ul className="divide-y divide-line">
                {q.data.items.map((n) => {
                  const Icon = ICONS[n.kind] ?? Bell;
                  return (
                    <li key={n.id}>
                      <button className={`flex w-full gap-3 px-4 py-3 text-left hover:bg-sunken ${n.read ? '' : 'bg-action-soft/40'}`} onClick={() => pick(n)}>
                        <Icon className={`mt-0.5 size-4 shrink-0 ${n.read ? 'text-muted' : 'text-action'}`} aria-hidden />
                        <span className="min-w-0 flex-1">
                          <span className={`block text-[14px] leading-snug ${n.read ? '' : 'font-semibold'}`}>{n.title}</span>
                          {n.body && <span className="mt-0.5 block text-[13px] text-muted">{n.body}</span>}
                          <span className="mt-0.5 block text-[12px] text-muted">{[n.teamName, timeAgo(n.createdAt)].filter(Boolean).join(' · ')}</span>
                        </span>
                        {!n.read && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-pink" aria-label="Unread" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="border-t border-line px-4 py-2.5 text-[13px]">
            <Link to="/account#notifications" className="font-medium text-action hover:underline" onClick={() => setOpen(false)}>
              Choose what you’re told about
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

/** Account page: a tickbox for the app and for email, per kind; instant or daily emails; teams turned off. */
export function NotificationSettingsSection() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['notification-settings'], queryFn: () => api.get<NotificationSettings>('/notifications/settings') });
  const [draft, setDraft] = useState<NotificationSettings | null>(null);
  useEffect(() => {
    if (q.data) setDraft(q.data);
  }, [q.data]);
  useEffect(() => {
    if (q.data && window.location.hash === '#notifications') document.getElementById('notifications')?.scrollIntoView({ behavior: 'smooth' });
  }, [q.data]);
  const save = useMutation({
    mutationFn: (s: NotificationSettings) => api.put<NotificationSettings>('/notifications/settings', { kinds: s.kinds, emailMode: s.emailMode, mutedTeamIds: s.mutedTeamIds }),
    onSuccess: (s) => {
      qc.setQueryData(['notification-settings'], s);
      setDraft(s);
    },
  });
  if (q.isLoading || !draft) return null;
  if (!draft.teams.length) return null; // Only for coaches.
  const dirty = JSON.stringify(draft) !== JSON.stringify(q.data);
  const setKind = (k: NotificationKind, field: 'app' | 'email', v: boolean) => setDraft({ ...draft, kinds: { ...draft.kinds, [k]: { ...draft.kinds[k], [field]: v } } });
  return (
    <div id="notifications" className="scroll-mt-20">
      <Section title="Notifications" description="What you’re told about, for the teams you coach. In the app, they show under the bell.">
        <div className="max-w-[640px] overflow-hidden rounded-lg border border-line">
          <table className="w-full text-left text-[14px]">
            <thead className="border-b border-line bg-surface text-[13px] text-muted">
              <tr>
                <th className="px-4 py-2 font-medium">When</th>
                <th className="w-20 px-2 py-2 text-center font-medium">In app</th>
                <th className="w-20 px-2 py-2 text-center font-medium">Email</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {NOTIFICATION_KINDS.map((k) => (
                <tr key={k}>
                  <td className="px-4 py-2.5">
                    <span className="font-medium">{NOTIFICATION_LABELS[k].title}</span>
                    <span className="block text-[13px] text-muted">{NOTIFICATION_LABELS[k].hint}</span>
                  </td>
                  {(['app', 'email'] as const).map((f) => (
                    <td key={f} className="px-2 py-2.5 text-center">
                      <input
                        type="checkbox"
                        className="size-5 accent-[var(--action)]"
                        checked={draft.kinds[k][f]}
                        onChange={(e) => setKind(k, f, e.target.checked)}
                        aria-label={`${NOTIFICATION_LABELS[k].title}: ${f === 'app' ? 'in app' : 'email'}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <fieldset className="mt-5 max-w-[640px] space-y-1.5">
          <legend className="mb-1 text-[14px] font-medium">Emails</legend>
          {(
            [
              ['instant', 'As they happen', 'One email for each.'],
              ['daily', 'One summary a day', 'Around 6 pm. A player who can’t make a session in the next day is still emailed straight away.'],
            ] as const
          ).map(([v, title, sub]) => (
            <label key={v} className={`flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5 ${draft.emailMode === v ? 'border-action bg-action-soft' : 'border-line hover:bg-sunken'}`}>
              <input type="radio" name="emailMode" className="mt-1 accent-[var(--action)]" checked={draft.emailMode === v} onChange={() => setDraft({ ...draft, emailMode: v })} />
              <span>
                <span className="block font-medium">{title}</span>
                <span className="block text-[13px] text-muted">{sub}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {draft.teams.length > 1 && (
          <fieldset className="mt-5 max-w-[640px]">
            <legend className="mb-1 text-[14px] font-medium">Teams</legend>
            <p className="mb-2 text-[13px] text-muted">Untick a team to hear nothing from it, say one you’re only helping out on.</p>
            {draft.teams.map((t) => (
              <label key={t.id} className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-sunken">
                <input
                  type="checkbox"
                  className="size-4 accent-[var(--action)]"
                  checked={!draft.mutedTeamIds.includes(t.id)}
                  onChange={(e) => setDraft({ ...draft, mutedTeamIds: e.target.checked ? draft.mutedTeamIds.filter((x) => x !== t.id) : [...draft.mutedTeamIds, t.id] })}
                />
                {t.name}
              </label>
            ))}
          </fieldset>
        )}

        {save.isError && (
          <div className="mt-4">
            <Alert>{errorMessage(save.error)}</Alert>
          </div>
        )}
        <div className="mt-5 flex items-center gap-3">
          <Button loading={save.isPending} disabled={!dirty} onClick={() => save.mutate(draft)}>
            Save notification settings
          </Button>
          {save.isSuccess && !dirty && <span className="text-[14px] text-ok">Saved</span>}
        </div>
      </Section>
    </div>
  );
}
